import { createContext, FC, PropsWithChildren, useContext, useMemo, useState } from 'react';

export type NotificationType = 'error' | 'warning' | 'info' | 'success';

export type Notification = {
  id?: number;
  type: NotificationType;
  message: string;
  solution?: string;
  details?: string;
  duration?: number;
};
type NotifierContextProps = {
  notifications: Notification[];
  addNotification: (notification: Notification) => number;
  removeNotification: (id: number) => void;
  removeAllErrorNotifications: () => void;
  removeAllWarningNotifications: () => void;
};
const NotifierContext = createContext<NotifierContextProps | undefined>(undefined);

export const useNotifierContext = () => {
  const value = useContext(NotifierContext);
  if (!value) {
    throw new Error('no value found for NotifierContext, did you forget to wrap it in a provider?');
  }
  return value;
};

export const NotifierContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const removeAllErrorNotifications = () => {
    setNotifications((prevState) => {
      const remaining = prevState.filter((n) => n.type !== 'error');
      return remaining;
    });
  };

  const addNotification = (notification: Notification) => {
    // TODO find better way to gen rundom number id
    const addedNotification = {
      ...notification,
      id: notification.id || Date.now(),
      duration: notification.type === 'success' ? 5000 : undefined
    };

    setNotifications((prevNotifications) => {
      const isDuplicate = prevNotifications.some(
        (existingNotification) => existingNotification.id === addedNotification.id
      );

      if (!isDuplicate) {
        return [...prevNotifications, addedNotification];
      }

      return prevNotifications;
    });
    return addedNotification.id;
  };

  const removeNotification = (id: number) => {
    setNotifications((prevNotifications) => prevNotifications.filter((n) => n.id !== id));
  };

  const removeAllWarningNotifications = () => {
    setNotifications((prevState) => {
      const remaining = prevState.filter((n) => n.type !== 'warning');
      return remaining;
    });
  };

  const value = useMemo(
    () => ({
      notifications,
      addNotification,
      removeNotification,
      removeAllErrorNotifications,
      removeAllWarningNotifications
    }),
    [notifications]
  );

  return <NotifierContext.Provider value={value}>{children}</NotifierContext.Provider>;
};
